import { DialogContent, Grid, Typography } from '@mui/material';
import { DocumentList } from 'components/_commons/Document/DocumentList';
import { FormInputField, FormTextareaField } from 'components/_commons/Form/Inputs';
import FormDatePicker from 'components/_commons/Form/Inputs/Datepicker/FormDatePicker';
import FormSelectField from 'components/_commons/Form/Inputs/SelectField/FormSelectField';
import { CustomActionButtons } from 'components/_commons/Modals/_CustomActionButtons';
import { ModalHeader } from 'components/_commons/Modals/_ModalHeader';
import { StyledInput } from 'components/_commons/Modals/common/SelectEntity';
import debounce from 'debounce-promise';
import { useFetch, useModal } from 'hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, FormContainer, useForm } from 'react-hook-form-mui';
import { AuditService, DocumentService, InstitutionService } from 'services';
import { enumOptionTypes, EnumService } from 'services/EnumService';
import { DocumentHelper, isTablet, translate } from 'utils';
import { AUDIT_TYPES } from 'utils/constants';
import { DateHelper } from 'utils/helpers';
import AsyncSelect from 'react-select/async';
import { useTheme } from '@mui/material/styles';
import { components } from 'react-select';

export const CreateAuditModal = ({
  onClose,
  onSubmit,
  defaultValues,
  withInstitutionToSelect
}) => {
  const theme = useTheme();
  const formContext = useForm({ defaultValues });
  const {
    watch,
    setValue,
    control,
    formState: { errors }
  } = formContext;
  const { response: auditStatuses } = useFetch(() => EnumService.getEnumOptions(enumOptionTypes.AUDIT_STATUS), []);
  const { response: auditTypes } = useFetch(() => EnumService.getEnumOptions(enumOptionTypes.AUDIT_TYPE), []);
  const { response: auditors } = useFetch(() => AuditService.getAuditors(), []);
  const [institutions, setInstitutions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { open } = useModal();

  const getInstitutionsPromise = useCallback((freeSearch) => new Promise((resolve, reject) => {
    setIsLoading(true);
    InstitutionService.getInstitutionOptions({ freeSearch })
      .then((response) => resolve(response))
      .catch((error) => reject((error && error.message) || error))
      .finally(() => setIsLoading(false));
  }), []);

  const debouncedFetchInstitutions = debounce(getInstitutionsPromise, 500);

  const fetchInstitutions = useCallback((freeSearch) => {
    debouncedFetchInstitutions(freeSearch)
      .then((resp) => setInstitutions(resp));
  }, [debouncedFetchInstitutions]);

  useEffect(() => {
    if (!defaultValues?.report?.base64Content && defaultValues?.report) {
      DocumentService.getFileTransferModel(defaultValues.report.id)
        .then((fileData) => formContext.setValue('report.base64Content', fileData.base64Content));
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchInstitutions('');
    // eslint-disable-next-line
  }, []);

  const handleAddReport = (doc) => {
    if (doc) {
      setValue('report', { name: doc.name, base64Content: DocumentHelper.getDocumentWithoutBase64(doc) });
    }
  };

  const handleDeleteReport = () => {
    setValue('report', null);
  };

  const submit = useCallback(() => {
    onSubmit(
      {
        ...formContext.getValues(),
        startDate: watch('startDate') ? DateHelper.getLocalDateTime(watch('startDate')) : null,
        appointment: DateHelper.getDateWithoutTimezoneTransformation(watch('appointment'))
      },
      setIsSaving
    );
  }, [onSubmit, formContext, watch]);

  const handleSubmit = () => {
    if (watch('type').value === AUDIT_TYPES.READING_COMMITTEE) {
      open({
        type: 'CONFIRM',
        onConfirm: () => submit(),
        title: 'modalAudit.action.save_reading_committee',
        content: translate('modalAudit.confirmation.save_reading_committee')
      });
    } else {
      submit();
    }
  };

  const CustomOption = (props, option) => (
    <components.Option {...props} key={option.id}>
      <Typography variant="h6">
        {props.label}
      </Typography>
    </components.Option>
  );

  const getBorderColor = (hasError, state) => {
    if (hasError) return theme.palette.error.main;
    if (state.isFocused) return theme.palette.primary.main;
    return theme.palette.grey[400];
  };

  const getInstitutionAsyncSelectStyles = (hasError = false) => ({
    control: (base, state) => ({
      ...base,
      minHeight: 56,
      borderRadius: theme.shape.borderRadius,
      borderColor: getBorderColor(hasError, state),
      boxShadow: state.isFocused
        ? `0 0 0 2px ${theme.palette.primary.main}33`
        : 'none',
      '&:hover': {
        borderColor: hasError
          ? theme.palette.error.main
          : theme.palette.text.primary
      },
      fontSize: '1.6rem',
      backgroundColor: theme.palette.background.paper
    }),
    menu: (base) => ({
      ...base,
      zIndex: 2000
    }),
    placeholder: (base) => ({
      ...base,
      color: hasError
        ? theme.palette.error.main
        : theme.palette.text.disabled,
      fontWeight: 400,
      fontSize: '1.6rem'
    }),
    option: (base, state) => ({
      ...base,
      padding: state.data.noPadding ? 0 : '0.5rem 1rem',
      cursor: 'pointer',
      backgroundColor: state.isFocused
        ? theme.palette.grey[100]
        : 'transparent',
      color: theme.palette.text.primary
    })
  });

  return (
    <FormContainer
      formContext={formContext}
      FormProps={{
        autoComplete: 'off',
        name: 'auditForm',
        style: { overflow: 'auto' }
      }}
      onSuccess={handleSubmit}
    >
      <ModalHeader onClose={onClose}>
        <Typography component="span">
          {defaultValues && defaultValues.id
            ? translate('modalAudit.editAudit')
            : translate('modalAudit.newAudit')}
        </Typography>
      </ModalHeader>

      <DialogContent style={{ width: isTablet() ? '90vw' : '60vw', paddingLeft: '3rem' }}>
        <Grid container direction="column">
          <Typography margin={1} paddingTop={2} variant="h5">
            {translate('modalAudit.auditSection')}
          </Typography>
          <Grid item width="50%">
            {withInstitutionToSelect && (
              <StyledInput>
                <Controller
                  control={control}
                  name="institution"
                  render={({ field }) => {
                    const error = errors.institution;
                    return (
                      <div style={{ marginBottom: '1rem' }}>
                        <AsyncSelect
                          cacheOptions
                          closeMenuOnSelect
                          components={{ Option: CustomOption }}
                          defaultOptions={institutions}
                          isClearable
                          isLoading={isLoading}
                          loadingMessage={() => translate('common.loading')}
                          loadOptions={(inputValue) => debouncedFetchInstitutions(inputValue)}
                          name="institution"
                          noOptionsMessage={() => translate('warnings.noOptionsAvailable')}
                          placeholder={`${translate('common.institution')} *`}
                          required
                          styles={getInstitutionAsyncSelectStyles(error)}
                          value={field.value}
                          onChange={(selectedOption) => field.onChange(selectedOption)}
                        />
                        {error && (
                          <div style={{ color: theme.palette.error.main, fontWeight: 400, fontSize: '1.2rem' }}>
                            {error?.message}
                          </div>
                        )}
                      </div>
                    );
                  }}
                  rules={{ required: translate('common.requiredField') }}
                />
              </StyledInput>
            )}
          </Grid>
          <Grid item width="50%">
            <FormSelectField
              label={translate('modalAudit.auditInformation.type')}
              name="type"
              options={auditTypes}
              required
            />
          </Grid>
          <Grid item width="50%">
            <FormSelectField
              label={translate('modalAudit.auditInformation.auditor')}
              name="auditor"
              options={auditors}
            />
          </Grid>
          <Grid item width="50%">
            <FormInputField
              fullWidth
              label={translate('modalAudit.auditInformation.auditorFreeField')}
              name="auditorFreeField"
            />
          </Grid>

          <Grid container direction="row" marginTop={1} spacing={5}>
            <Grid item>
              <FormDatePicker
                label="modalAudit.auditInformation.appointment"
                name="appointment"
                required
              />
            </Grid>
            <Grid item>
              <FormDatePicker
                disableFuture
                label="modalAudit.auditInformation.startDate"
                name="startDate"
                validate={(value) => (value ? DateHelper.validateIfDateIsValid(value) : true)}
              />
            </Grid>
          </Grid>

          <Typography margin={1} paddingTop={2} variant="h5">
            {translate('modalAudit.conclusionSection')}
          </Typography>

          <Grid item width="50%">
            <FormSelectField
              label={translate('modalAudit.auditInformation.status')}
              name="status"
              options={auditStatuses}
            />
          </Grid>
          <FormTextareaField
            label={translate('modalAudit.auditInformation.reason')}
            name="reason"
          />
          <FormTextareaField
            label={translate('modalAudit.auditInformation.comment')}
            name="comment"
          />
          <FormTextareaField
            label={translate('modalAudit.auditInformation.nonCompliances')}
            name="nonCompliances"
          />
          <FormTextareaField
            label={translate('modalAudit.auditInformation.privateComment')}
            name="privateComment"
          />

          <Typography margin={1} paddingBottom={1} paddingTop={2} variant="h5">
            {translate('modalAudit.reportSection')}
          </Typography>
          <Grid item>
            <DocumentList
              canEdit={!defaultValues?.report?.id}
              documents={watch('report') ? [{ document: watch('report') }] : []}
              importLabel="button.import"
              sizeLimit={1}
              spacing={2.5}
              titleStyles={{
                textAlign: 'center',
                variant: 'h6'
              }}
              onDelete={handleDeleteReport}
              onImport={handleAddReport}
            />
          </Grid>

        </Grid>
      </DialogContent>

      <CustomActionButtons
        isSaving={isSaving}
        onClose={onClose}
      />

    </FormContainer>
  );
};
